import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Plus, Store, X, Sparkles, ShoppingCart, Radar } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProductDetail from "@/components/ProductDetail";
import MarketplaceFilters from "@/components/MarketplaceFilters";
import MarketInsightsPanel from "@/components/MarketInsightsPanel";

const SEED = [
  { title: "Holographic Lens Pack", description: "12 premium cinematic LUTs for the NEXUS aesthetic.", price: 29.0, image_url: "https://images.unsplash.com/photo-1772618358401-77af868a21d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxoaWdoJTIwZW5kJTIwdGVjaCUyMHByb2R1Y3R8ZW58MHx8fHwxNzgyNDE0NDI4fDA&ixlib=rb-4.1.0&q=85", category: "digital" },
  { title: "Aurora Brand Kit", description: "Adaptive identity system in Indigo / Cyan / Purple.", price: 49.0, image_url: "https://images.unsplash.com/photo-1636690513351-0af1763f6237?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBnZW5lcmF0aXZlJTIwYXJ0fGVufDB8fHx8MTc4MjQxNDQyOHww&ixlib=rb-4.1.0&q=85", category: "digital" },
  { title: "Quantum Audio Pack", description: "32 ambient stems for AI video scoring.", price: 19.0, image_url: "https://images.pexels.com/photos/18446974/pexels-photo-18446974.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", category: "audio" },
];

export default function BusinessZone() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", price: 9.99, image_url: "", category: "digital" });
  const [busy, setBusy] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [advisorPrompt, setAdvisorPrompt] = useState("");
  const [advisorReply, setAdvisorReply] = useState("");
  const [advisorBusy, setAdvisorBusy] = useState(false);
  const [demand, setDemand] = useState([]);

  // --- search + filters ---
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("recent");
  const [facets, setFacets] = useState({ categories: [], price_range: { min: 0, max: 0 }, total: 0 });

  const buildParams = () => {
    const params = {};
    if (query.trim()) params.q = query.trim();
    if (category && category !== "all") params.category = category;
    if (minPrice !== "") params.min_price = Number(minPrice);
    if (maxPrice !== "") params.max_price = Number(maxPrice);
    if (sort) params.sort = sort;
    return params;
  };

  const load = async () => {
    const r = await api.get("/products", { params: buildParams() });
    let items = r.data;
    // Seed only when the catalogue is genuinely empty (no filters either).
    const noFilters = !query && (!category || category === "all") && minPrice === "" && maxPrice === "" && sort === "recent";
    if (items.length === 0 && noFilters && facets.total === 0) {
      try {
        for (const s of SEED) await api.post("/products", s);
        const r2 = await api.get("/products");
        items = r2.data;
      } catch (e) {
        console.warn("[BusinessZone] seed failed", e);
      }
    }
    setProducts(items);
    // Fire a search-analytics impression beacon (only when a real query/category was used).
    if (query || (category && category !== "all")) {
      api.post("/products/search/event", {
        q: query || null,
        category: category && category !== "all" ? category : null,
        result_count: items.length,
        clicked_product_id: null,
      }).catch(() => {});
    }
  };

  const loadFacets = async () => {
    try {
      const r = await api.get("/products/facets");
      setFacets(r.data);
    } catch (e) {
      console.warn("[BusinessZone] facets failed", e);
    }
  };

  // Debounce filter changes so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [query, category, minPrice, maxPrice, sort]);

  useEffect(() => { loadFacets(); }, []);

  // Load demand-radar (zero-result queries) once per mount.
  useEffect(() => {
    api.get("/products/demand-radar?days=14")
      .then((r) => setDemand(r.data?.gaps || []))
      .catch(() => {});
  }, []);

  const clearFilters = () => {
    setQuery(""); setCategory("all"); setMinPrice(""); setMaxPrice(""); setSort("recent");
  };

  const resultCount = useMemo(() => products.length, [products]);

  // Click-beacon wrapper — fires a search-analytics click event when the user
  // opens a product from a query/category-filtered result set.
  const openDetail = (p) => {
    if (query || (category && category !== "all")) {
      api.post("/products/search/event", {
        q: query || null,
        category: category && category !== "all" ? category : null,
        result_count: products.length,
        clicked_product_id: p.id,
      }).catch(() => {});
    }
    setDetail(p);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price) });
      setShowCreate(false);
      setForm({ title: "", description: "", price: 9.99, image_url: "", category: "digital" });
      await Promise.all([load(), loadFacets()]);
      toast.success("Listed");
    } catch { toast.error("Failed to list"); }
    setBusy(false);
  };

  const buy = async (product, negotiationToken = null) => {
    try {
      const r = await api.post("/checkout/session", {
        product_id: product.id,
        origin_url: window.location.origin,
        negotiation_token: negotiationToken,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  const askAdvisor = async () => {
    if (!advisorPrompt.trim()) return;
    setAdvisorBusy(true);
    setAdvisorReply("");
    try {
      const r = await api.post("/ai/chat", {
        message: advisorPrompt,
        provider: "anthropic",
        system: "You are an AI Business Advisor for a digital marketplace. Be concise, give pricing logic, branding angles, and target audience. Use bullets.",
      });
      setAdvisorReply(r.data.reply);
    } catch { toast.error("Advisor unreachable"); }
    setAdvisorBusy(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-1">business & marketplace</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Your digital storefront</h1>
          <p className="text-[#64748B] text-sm mt-1">Negotiate, list, sell — globally, instantly.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="open-advisor"
            onClick={() => setAdvisorOpen(true)}
            className="nexus-btn-secondary text-sm flex items-center gap-2"
          >
            <Sparkles size={14} /> AI Advisor
          </button>
          <button
            data-testid="new-product-btn"
            onClick={() => setShowCreate(true)}
            className="nexus-btn-primary text-sm flex items-center gap-2"
          >
            <Plus size={14} /> List product
          </button>
        </div>
      </div>

      {/* Global anonymized marketplace pulse — trending queries + top categories */}
      <MarketInsightsPanel
        defaultDays={14}
        onCategoryClick={(c) => setCategory(c)}
        onQueryClick={(q) => setQuery(q)}
      />

      {/* Demand radar — top zero-result queries (market gaps the seller can fill) */}
      {demand.length > 0 && (
        <div data-testid="demand-radar" className="glass-heavy rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Radar size={14} className="text-[#00E5FF]" />
            <div className="font-display font-bold text-sm">Demand radar</div>
            <div className="text-xs text-[#94A3B8]">Buyers searched for these — and found nothing. List something here and you&rsquo;re the only seller.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {demand.slice(0, 12).map((g, i) => (
              <button
                key={`gap-${i}`}
                data-testid={`demand-gap-${i}`}
                onClick={() => { setShowCreate(true); setForm((f) => ({ ...f, title: g.q })); }}
                className="px-3 py-1.5 rounded-full glass hover:scale-[1.03] transition flex items-center gap-2 text-xs"
                title="Tap to start a listing pre-filled with this title"
              >
                <span className="font-bold text-[#0F172A]">&ldquo;{g.q}&rdquo;</span>
                <span className="text-[10px] uppercase tracking-wider text-[#00E5FF] font-bold">{g.searches}× searched</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        <aside className="lg:sticky lg:top-4 lg:self-start">
          <MarketplaceFilters
            query={query} onQueryChange={setQuery}
            category={category} onCategoryChange={setCategory}
            minPrice={minPrice} onMinPriceChange={setMinPrice}
            maxPrice={maxPrice} onMaxPriceChange={setMaxPrice}
            sort={sort} onSortChange={setSort}
            facets={facets}
            resultCount={resultCount}
            onClear={clearFilters}
          />
        </aside>

        <div data-testid="products-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.length === 0 ? (
            <div
              data-testid="marketplace-empty"
              className="col-span-full glass rounded-2xl p-10 text-center text-[#64748B]"
            >
              <Store size={28} className="mx-auto mb-3 text-[#94A3B8]" />
              <div className="font-display text-lg text-[#0F172A] mb-1">No products match</div>
              <div className="text-sm">Adjust your filters or clear them to see the full catalogue.</div>
            </div>
          ) : products.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="group glass rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-2xl hover:shadow-[rgba(221,42,123,0.18)] transition-all duration-300"
            >
              {p.image_url ? (
                <button onClick={() => openDetail(p)} data-testid={`open-detail-${p.id}`} className="block relative overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                    <span className="text-white text-xs font-semibold tracking-wide">Tap to preview →</span>
                  </div>
                </button>
              ) : (
                <button onClick={() => openDetail(p)} className="w-full h-44 bg-gradient-to-br from-[#FF8A65]/25 via-[#DD2A7B]/25 to-[#8134AF]/25 flex items-center justify-center transition-all group-hover:from-[#FF8A65]/40 group-hover:via-[#DD2A7B]/40 group-hover:to-[#8134AF]/40">
                  <Store size={32} className="text-[#DD2A7B] transition-transform group-hover:scale-110" />
                </button>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#DD2A7B] font-semibold mb-1">{p.category}</div>
                <button onClick={() => openDetail(p)} className="text-left">
                  <div className="font-display font-bold text-lg leading-tight group-hover:text-[#DD2A7B] transition-colors">{p.title}</div>
                </button>
                <div className="text-sm text-[#64748B] mt-1 line-clamp-2">{p.description}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="font-display text-xl font-bold text-[#0F172A]">${p.price.toFixed(2)}</div>
                  <button
                    data-testid={`buy-${p.id}`}
                    onClick={() => openDetail(p)}
                    className="nexus-btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
                  >
                    <ShoppingCart size={12} /> View
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-[#94A3B8]">by {p.seller_name}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {detail && (
        <ProductDetail product={detail} onClose={() => setDetail(null)} onBuy={buy} />
      )}

      {/* Create modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(8px)" }}
          onClick={() => setShowCreate(false)}
        >
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="glass-heavy rounded-3xl p-6 max-w-md w-full grain"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold">New product</h3>
              <button type="button" onClick={() => setShowCreate(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <input required data-testid="pf-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="nexus-input" />
              <textarea required data-testid="pf-desc" placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="nexus-input resize-none" />
              <input required type="number" step="0.01" min="0.5" data-testid="pf-price" placeholder="Price (USD)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="nexus-input" />
              <input data-testid="pf-image" placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="nexus-input" />
              <select
                data-testid="pf-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="nexus-input"
              >
                <option value="digital">digital</option>
                <option value="audio">audio</option>
                <option value="video">video</option>
                <option value="template">template</option>
                <option value="course">course</option>
                <option value="service">service</option>
              </select>
              <button data-testid="pf-submit" disabled={busy} className="w-full nexus-btn-primary">{busy ? "…" : "List"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Advisor drawer */}
      {advisorOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-end"
          style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(6px)" }}
          onClick={() => setAdvisorOpen(false)}
        >
          <motion.div
            initial={{ x: 80, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-heavy h-full w-full sm:w-[440px] p-6 overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542]">advisor</div>
                <h3 className="font-display font-bold text-xl">AI Business Advisor</h3>
              </div>
              <button onClick={() => setAdvisorOpen(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <textarea
              data-testid="advisor-prompt"
              value={advisorPrompt}
              onChange={(e) => setAdvisorPrompt(e.target.value)}
              placeholder="e.g. How should I price a digital art bundle for creators?"
              rows={4}
              className="nexus-input resize-none"
            />
            <button data-testid="advisor-ask" onClick={askAdvisor} disabled={advisorBusy} className="nexus-btn-primary w-full mt-3">
              {advisorBusy ? "Thinking…" : "Get strategy"}
            </button>
            {advisorReply && (
              <div className="mt-5 glass rounded-2xl p-4 whitespace-pre-wrap text-sm leading-relaxed text-[#0F172A]">
                {advisorReply}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
